import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { billingApi, billingEnabled } from './api';

export const BillingAccessContext = createContext(null);
export const useBillingAccess = () => useContext(BillingAccessContext);
export function BillingAccessBoundary({ children }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const requestVersion = useRef(0);
  const refreshStatus = useCallback(async () => {
    const version = ++requestVersion.current;
    try {
      const next = await billingApi.status();
      if (version === requestVersion.current) { setStatus(next); setError(''); }
    } catch (failure) {
      if (version === requestVersion.current) setError(failure.message);
    }
  }, []);
  useEffect(() => {
    if (!billingEnabled) return;
    refreshStatus();
    const refresh = () => { if (document.visibilityState !== 'hidden') refreshStatus(); };
    const timer = setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    return () => { requestVersion.current++; clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [refreshStatus]);
  if (!billingEnabled) return children;
  if (error) return <Box p={4}><Alert severity="error">{error}</Alert><Button onClick={refreshStatus}>Retry billing access</Button></Box>;
  if (!status) return <Box p={4}><CircularProgress aria-label="Checking billing setup" /></Box>;
  if (status.service_state === 'plan_required' && status.can_complete_setup) return <Navigate to="/choose-plan" replace />;
  if (status.service_state === 'pending_setup' && status.can_complete_setup) return <Navigate to="/billing-setup" replace />;
  if (status.service_state !== 'active') return <Box p={4}><Alert severity="info">Your firm’s owner needs to choose a plan and complete account setup.</Alert></Box>;
  return <BillingAccessContext.Provider value={{ status, refreshStatus }}><React.Fragment key={JSON.stringify(status.capabilities)}>{children}</React.Fragment></BillingAccessContext.Provider>;
}
